import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../features/auth/store";
import { getCurrentUser, updateCurrentUser } from "../../features/auth/api";
import { getFollowerUsers, getFollowingUsers } from "../../entities/user/api";
import { IS_API_MODE } from "../../shared/config/runtime";
import { getApiErrorMessage } from "../../shared/api/errors";
import { exploreWorks } from "../../shared/data/mockData";
import PosterArtwork from "../../entities/post/ui/PosterArtwork";
import { Eye, EyeOff, FileImage, Heart } from "lucide-react";
import { listPostDrafts, listPosts } from "../../entities/post/api";
import { adaptPost } from "../../entities/post/adapter";
import defaultAvatar from "../../shared/ui/defaultAvatar";

function getDraftCover(draft) {
  const mediaItems = Array.isArray(draft.media_items) ? draft.media_items : [];
  const cover = mediaItems.find((item) => item.is_cover)
    || [...mediaItems].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))[0];
  return cover?.file_url || cover?.fileUrl || "";
}

function formatDraftTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export default function ProfilePage({ editable = false }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const [editing, setEditing] = useState(editable);
  const [activeTab, setActiveTab] = useState("posts");
  const [form, setForm] = useState({ nickname: "", signature: "" });
  const profileQuery = useQuery({
    queryKey: ["profile", "me"],
    queryFn: getCurrentUser,
    enabled: IS_API_MODE,
  });
  const profile = profileQuery.data || user;
  const followingQuery = useQuery({
    queryKey: ["users", profile?.id, "following"],
    queryFn: () => getFollowingUsers(profile.id),
    enabled: IS_API_MODE && Boolean(profile?.id),
  });
  const followersQuery = useQuery({
    queryKey: ["users", profile?.id, "followers"],
    queryFn: () => getFollowerUsers(profile.id),
    enabled: IS_API_MODE && Boolean(profile?.id),
  });
  const authoredPostsQuery = useQuery({
    queryKey: ["posts", "profile", profile?.id],
    queryFn: () => listPosts({ pageSize: 100 }),
    enabled: IS_API_MODE && Boolean(profile?.id),
  });
  const draftsQuery = useQuery({
    queryKey: ["drafts", "profile"],
    queryFn: () => listPostDrafts({ pageSize: 100 }),
    enabled: IS_API_MODE && activeTab === "drafts",
  });
  const avatarUrl = profile?.avatar_url || profile?.avatarUrl || defaultAvatar;
  const followerCount = profile?.follower_count ?? profile?.followerCount ?? 0;
  const followingCount = profile?.following_count ?? profile?.followingCount ?? 0;
  const followingItems = followingQuery.data?.results || followingQuery.data || [];
  const followerItems = followersQuery.data?.results || followersQuery.data || [];
  const followingTotal = followingQuery.data ? followingItems.length : followingCount;
  const followerTotal = followersQuery.data ? followerItems.length : followerCount;
  const authoredPosts = authoredPostsQuery.data?.results || authoredPostsQuery.data || [];
  const ownPosts = authoredPosts.filter((post) => String(post.author?.id || post.author_id) === String(profile?.id));
  const draftItems = (draftsQuery.data?.results || draftsQuery.data || [])
    .filter((draft) => String(draft.status || "EDITING").toUpperCase() === "EDITING");
  const tabPosts = activeTab === "posts"
    ? ownPosts
    : activeTab === "saved"
      ? authoredPosts.filter((post) => Boolean(post.is_collected))
      : activeTab === "liked"
        ? authoredPosts.filter((post) => Boolean(post.is_liked))
        : [];
  const likedTotal = authoredPostsQuery.data
    ? ownPosts
        .reduce((total, post) => total + Number(post.like_count || 0), 0)
    : Number(profile?.like_count ?? profile?.liked_count ?? 0);
  const updateMutation = useMutation({
    mutationFn: updateCurrentUser,
    onSuccess: (updated) => {
      queryClient.setQueryData(["profile", "me"], updated);
      setEditing(false);
    },
  });

  if (IS_API_MODE && profileQuery.isPending) {
    return <div className="detail-state">正在加载个人资料……</div>;
  }
  if (IS_API_MODE && profileQuery.isError) {
    return <div className="detail-state error">{getApiErrorMessage(profileQuery.error, "个人资料加载失败")}</div>;
  }

  function saveProfile(event) {
    event.preventDefault();
    updateMutation.mutate(form);
  }

  return (
    <section className="profile-page">
      <div className="profile-content">
        <header className="profile-header">
          <img className="profile-avatar" src={avatarUrl} alt="" />
          <div className="profile-main">
            <div className="profile-title">
              <div>
                <h1>{profile?.nickname || profile?.username}</h1>
                <span>@{profile?.username}</span>
              </div>
            </div>
            <p>{profile?.signature || "还没有填写个人简介"}</p>
            <div className="profile-chips">
              <span>✦ 平面设计</span>
              <span>📍 杭州</span>
              <span>✓ 已完成 12 次服务</span>
            </div>
            <div className="profile-stats">
              <span><b>{followingTotal}</b> 关注</span>
              <span><b>{followerTotal}</b> 粉丝</span>
              <span><b>{likedTotal}</b> 获赞</span>
            </div>
          </div>
          </header>
          {editing ? (
            <form className="form-card profile-edit-form" onSubmit={saveProfile}>
              <label>
                <span>昵称</span>
                <input value={form.nickname} onChange={(event) => setForm({ ...form, nickname: event.target.value })} required />
              </label>
              <label>
                <span>个人简介</span>
                <textarea value={form.signature} onChange={(event) => setForm({ ...form, signature: event.target.value })} />
              </label>
              {updateMutation.isError ? <p className="form-error">{getApiErrorMessage(updateMutation.error, "资料保存失败")}</p> : null}
              <div className="form-actions">
                <button className="ghost-button" type="button" onClick={() => setEditing(false)}>取消</button>
                <button className="primary-button" type="submit" disabled={updateMutation.isPending}>{updateMutation.isPending ? "保存中……" : "保存资料"}</button>
              </div>
            </form>
          ) : null}
        <div className="profile-tabs">
          <button className={activeTab === "posts" ? "active" : ""} onClick={() => setActiveTab("posts")}>帖子</button>
          <button className={activeTab === "drafts" ? "active" : ""} onClick={() => setActiveTab("drafts")}>草稿</button>
          <button className={activeTab === "saved" ? "active" : ""} onClick={() => setActiveTab("saved")}>收藏</button>
          <button className={activeTab === "liked" ? "active" : ""} onClick={() => setActiveTab("liked")}>点赞</button>
          <button className={activeTab === "reviews" ? "active" : ""} onClick={() => setActiveTab("reviews")}>服务评价</button>
        </div>
        <div className="profile-grid">
          {activeTab === "drafts"
            ? draftItems.map((draft) => {
                const coverUrl = getDraftCover(draft);
                return (
                  <button
                    className="profile-draft-tile"
                    key={draft.id}
                    type="button"
                    onClick={() => navigate(`/create?draft=${draft.id}`)}
                  >
                    {coverUrl ? (
                      <img src={coverUrl} alt={draft.title || "未命名草稿"} />
                    ) : (
                      <span className="profile-draft-placeholder">
                        <FileImage size={30} />
                      </span>
                    )}
                    <span className="profile-draft-badge">草稿</span>
                    <div className="profile-draft-meta">
                      <strong>{draft.title || "未命名草稿"}</strong>
                      <small>{formatDraftTime(draft.updated_at || draft.updatedAt)}</small>
                    </div>
                  </button>
                );
              })
            : (IS_API_MODE ? tabPosts.map(adaptPost) : exploreWorks.slice(0, 6)).map((item, index) => (
                <button className="profile-post-tile" key={`${item.id || item.title}-${index}`} onClick={() => IS_API_MODE ? navigate(`/posts/${item.id}`, { state: { backgroundLocation: location } }) : null}>
                  {IS_API_MODE ? (
                    item.image ? <img src={item.image} alt={`${item.author}的设计作品`} /> : <PosterArtwork artwork={item.artwork} compact />
                  ) : <PosterArtwork artwork={item} compact />}
                  <span className="profile-post-overlay">
                    <span className={(item.liked || item.is_liked) ? "profile-like-state liked" : "profile-like-state"}><Heart size={18} fill={(item.liked || item.is_liked) ? "currentColor" : "none"} /> {new Intl.NumberFormat("zh-CN").format(item.likes || item.like_count || 0)}</span>
                  </span>
                  {activeTab === "posts" && IS_API_MODE ? <span className="visibility-status" title={String(item.reviewStatus || item.review_status || item.status || "公开").toLowerCase().includes("hidden") ? "隐藏" : "公开"}>{String(item.reviewStatus || item.review_status || item.status || "公开").toLowerCase().includes("hidden") ? <EyeOff size={18} /> : <Eye size={18} />}</span> : null}
                </button>
              ))}
        </div>
        {IS_API_MODE && activeTab === "drafts" && draftsQuery.isError ? (
          <p className="profile-empty error">{getApiErrorMessage(draftsQuery.error, "草稿加载失败")}</p>
        ) : null}
        {IS_API_MODE && (
          activeTab === "drafts"
            ? draftsQuery.isFetched && draftItems.length === 0
            : authoredPostsQuery.isFetched && tabPosts.length === 0
        ) ? (
          <p className="profile-empty">{activeTab === "posts" ? "还没有发布作品" : activeTab === "drafts" ? "还没有草稿" : activeTab === "saved" ? "还没有收藏作品" : activeTab === "liked" ? "还没有点赞作品" : "暂无服务评价"}</p>
        ) : null}
      </div>
    </section>
  );
}
